import React from 'react';
import { Helmet } from 'react-helmet-async';

import { site } from '../data/site';

/**
 * Every routed page's <head> in one place.
 *
 * public/index.html marks description, canonical and og:title/description/url
 * with data-rh so Helmet adopts them instead of leaving a duplicate behind.
 * That only holds while every page declares all five: a page that forgets one
 * ends up with the tag deleted rather than overridden. Routing the whole site
 * through this component is what makes that true by construction, and
 * seo-head.smoke.test.js is what keeps it true.
 *
 * `schema` takes either one JSON-LD object or an array of them; it is emitted
 * as a single @graph so a page never ships two competing script blocks.
 */
export default function Seo({ title, description, keywords, path = '/', schema, noindex }) {
    const url = `${site.origin}${path}`;
    const fullTitle = path === '/' ? `${site.name} | ${title}` : `${title} | ${site.name}`;
    const graph = schema ? (Array.isArray(schema) ? schema : [schema]) : null;

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {keywords && <meta name="keywords" content={keywords} />}
            {/* A noindex page gets no canonical. Pointing the 404 at the
                home page tells Google the two are the same document, which
                is the textbook way to turn a 404 into a soft 404. */}
            {noindex
                ? <meta name="robots" content="noindex, follow" />
                : <link rel="canonical" href={url} />}
            {!noindex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}

            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={url} />

            {graph && (
                <script type="application/ld+json">
                    {JSON.stringify({ '@context': 'https://schema.org', '@graph': graph })}
                </script>
            )}
        </Helmet>
    );
}
